import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Download, FileText, LogOut, ArrowLeft, Eye, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import {
  adminLogin,
  adminIntakes,
  adminIntakeDetail,
  adminDeleteIntake,
  reportPdfUrl,
} from "@/lib/api";

const STORAGE_KEY = "techport_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [intakes, setIntakes] = useState([]);
  const [detail, setDetail] = useState(null); // {item, full}
  const [detailLoading, setDetailLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchIntakes = async (t) => {
    try {
      const data = await adminIntakes(t);
      setIntakes(data);
    } catch {
      setToken("");
      localStorage.removeItem(STORAGE_KEY);
      toast.error("Sessie verlopen, log opnieuw in.");
    }
  };

  useEffect(() => {
    if (token) fetchIntakes(token);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await adminLogin(password);
      localStorage.setItem(STORAGE_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch {
      toast.error("Onjuist wachtwoord.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setIntakes([]);
  };

  const openDetail = async (item) => {
    setDetail({ item, full: null });
    setDetailLoading(true);
    try {
      const full = await adminIntakeDetail(token, item.id);
      setDetail({ item, full });
    } catch {
      toast.error("Kon details niet laden.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setDetail(null);

  const handleDelete = async (item) => {
    const label = item.name || item.email || item.first_answer_preview || "deze intake";
    if (!window.confirm(`Weet je zeker dat je "${label}" wilt verwijderen?\n\nDit kan niet ongedaan gemaakt worden.`)) {
      return;
    }
    setDeletingId(item.id);
    try {
      await adminDeleteIntake(token, item.id);
      setIntakes((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Intake verwijderd.");
    } catch {
      toast.error("Verwijderen mislukt.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-white grid grid-cols-12">
        <div className="hidden lg:block col-span-7 ink-bg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-20" style={{background: "radial-gradient(circle at 70% 30%, #3FCBA6 0%, transparent 60%)"}} />
          <div className="relative h-full flex flex-col justify-between p-12">
            <Brand variant="dark" />
            <div>
              <div className="font-condensed uppercase tracking-[0.3em] text-[13px] text-[#3FCBA6] font-semibold mb-6">
                · admin · beveiligd ·
              </div>
              <h1 className="font-display text-white text-7xl xl:text-9xl leading-[0.95]">
                Intake<br />
                <span className="italic-em" style={{ fontStyle: "italic" }}>dashboard.</span>
              </h1>
              <p className="text-white/60 mt-8 max-w-md text-lg">
                Bekijk alle deelnemers, transcripten en gegenereerde rapporten op één plek.
              </p>
            </div>
            <div className="font-condensed uppercase text-xs tracking-[0.25em] text-white/30 font-semibold">
              Techport · v1.0
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 flex items-center justify-center p-8 bg-white">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <div className="lg:hidden mb-8"><Brand /></div>
            <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#6B6B85] font-semibold mb-6 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" strokeWidth={2.5} />
              · authenticatie vereist ·
            </div>
            <h2 className="font-display text-5xl text-[#1B1B3A] mb-3">
              Log in
            </h2>
            <p className="text-[#1B1B3A]/70 mb-10">
              Voer het admin-wachtwoord in om door te gaan.
            </p>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Wachtwoord"
                className="w-full bg-white border-2 border-[#1B1B3A] px-5 py-4 text-lg text-[#1B1B3A] focus:outline-none focus:bg-[#3FCBA6]/10 transition-colors font-medium"
                data-testid="admin-password-input"
                autoFocus
              />
              <button
                type="submit"
                disabled={loading || !password}
                className="tp-btn tp-btn--mint w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-login-button"
              >
                {loading ? "Controleren..." : "Inloggen"}
              </button>
            </form>
            <Link
              to="/"
              className="flex items-center gap-2 font-condensed uppercase text-sm tracking-[0.15em] font-semibold text-[#6B6B85] hover:text-[#1B1B3A] mt-8 transition-colors"
              data-testid="admin-back-home"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
              Terug naar site
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  const completed = intakes.filter((i) => i.has_report).length;
  const inProgress = intakes.length - completed;
  const completionRate = intakes.length ? Math.round((completed / intakes.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b-2 border-[#1B1B3A] bg-white sticky top-0 z-20">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-4">
            <div className="font-condensed uppercase text-xs tracking-[0.2em] text-[#3FCBA6] font-semibold flex items-center gap-2">
              <span className="w-2 h-2 bg-[#3FCBA6]" />
              Admin
            </div>
            <button
              onClick={handleLogout}
              className="tp-btn--ghost inline-flex items-center gap-2 px-4 py-2 font-condensed uppercase text-xs tracking-[0.15em] font-semibold border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={2.5} />
              Uitloggen
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 lg:px-12 py-16">
        <div className="font-condensed uppercase tracking-[0.25em] text-sm text-[#6B6B85] font-semibold mb-6">
          · overzicht ·
        </div>
        <h1 className="font-display text-6xl lg:text-8xl text-[#1B1B3A] mb-3 leading-[0.95]">
          Intake <span className="italic-em" style={{ fontStyle: "italic" }}>records.</span>
        </h1>
        <p className="text-[#1B1B3A]/70 mb-12 text-lg">
          Alle deelnemers en hun AI-actieplannen. <span className="font-semibold">Tip:</span> klik op "Bekijk" om het volledige gesprek te zien — ook van deelnemers die de intake nog niet hebben afgemaakt.
        </p>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
          {[
            { label: "Totaal intakes", value: intakes.length, code: "01" },
            { label: "Afgerond met rapport", value: completed, code: "02", accent: true },
            { label: "Conversie", value: `${completionRate}%`, code: "03" },
          ].map((s) => (
            <div key={s.label} className={`border-2 border-[#1B1B3A] p-8 ${s.accent ? "mint-bg" : "bg-white"}`}>
              <div className="font-condensed uppercase text-xs tracking-[0.25em] font-semibold text-[#1B1B3A]/70 mb-3">
                · {s.code} · {s.label} ·
              </div>
              <div className="font-display text-7xl text-[#1B1B3A]">
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="border-2 border-[#1B1B3A] overflow-hidden">
          <div className="bg-[#1B1B3A] text-white px-6 py-4 flex items-center justify-between">
            <div className="font-display text-xl">
              Records · {intakes.length.toString().padStart(3, "0")}
            </div>
            <div className="font-condensed uppercase text-xs tracking-[0.25em] text-white/60 font-semibold">
              Sorted: nieuwste eerst
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="intakes-table">
              <thead className="border-b-2 border-[#1B1B3A] bg-[#F8F8FA]">
                <tr className="text-left">
                  {["#", "Naam / Eerste antwoord", "E-mail", "Datum", "Land", "Berichten", "Status", "Acties"].map((h) => (
                    <th key={h} className="px-6 py-4 font-condensed uppercase text-xs tracking-[0.2em] text-[#1B1B3A] font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {intakes.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-20 text-center font-condensed uppercase text-sm tracking-[0.2em] text-[#6B6B85] font-semibold"
                      data-testid="intakes-empty"
                    >
                      · Nog geen intakes ·
                    </td>
                  </tr>
                )}
                {intakes.map((item, idx) => (
                  <tr
                    key={item.id}
                    className="border-b border-[#E8E8EE] last:border-0 hover:bg-[#3FCBA6]/10 transition-colors"
                    data-testid={`intake-row-${item.id}`}
                  >
                    <td className="px-6 py-5 font-display text-lg text-[#3FCBA6] align-top">
                      {String(intakes.length - idx).padStart(3, "0")}
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div className="font-display text-lg text-[#1B1B3A] leading-tight">
                        {item.name || (item.first_answer_preview ? "—" : "Niet gestart")}
                      </div>
                      {item.first_answer_preview && (
                        <div className="text-xs text-[#6B6B85] mt-1 max-w-md italic" data-testid={`intake-preview-${item.id}`}>
                          "{item.first_answer_preview}"
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-5 text-[#1B1B3A]/85 align-top">{item.email || "—"}</td>
                    <td className="px-6 py-5 font-condensed text-[#6B6B85] text-sm align-top">
                      {new Date(item.created_at).toLocaleString("nl-NL", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </td>
                    <td className="px-6 py-5 align-top">
                      {item.country ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-[#F8F8FA] border border-[#E8E8EE] font-condensed uppercase text-[10px] tracking-[0.15em] font-bold text-[#1B1B3A]" title={item.ip_address || ""}>
                          {item.country}
                        </span>
                      ) : (
                        <span className="text-[#A1A1AA] text-xs">—</span>
                      )}
                    </td>
                    <td className="px-6 py-5 font-condensed text-[#1B1B3A] text-sm align-top">
                      {item.message_count}
                    </td>
                    <td className="px-6 py-5 align-top">
                      <span
                        className={
                          item.status === "completed"
                            ? "inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#3FCBA6] text-[#1B1B3A] font-condensed uppercase text-[11px] tracking-[0.18em] font-bold"
                            : "inline-flex items-center gap-1.5 px-3 py-1.5 border-2 border-[#1B1B3A] text-[#1B1B3A] font-condensed uppercase text-[11px] tracking-[0.18em] font-bold"
                        }
                      >
                        {item.status === "completed" ? "Afgerond" : "In voortgang"}
                      </span>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          onClick={() => openDetail(item)}
                          className="inline-flex items-center gap-1.5 text-[#1B1B3A] hover:text-[#3FCBA6] font-condensed uppercase text-xs tracking-[0.18em] font-bold transition-colors"
                          data-testid={`intake-view-${item.id}`}
                          title="Bekijk volledige intake"
                        >
                          <Eye className="w-3.5 h-3.5" strokeWidth={2.5} />
                          Bekijk
                        </button>
                        {item.has_report && (
                          <a
                            href={reportPdfUrl(item.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-[#1B1B3A] hover:text-[#3FCBA6] font-condensed uppercase text-xs tracking-[0.18em] font-bold"
                            data-testid={`intake-download-${item.id}`}
                            title="Download PDF rapport"
                          >
                            <Download className="w-3.5 h-3.5" strokeWidth={2.5} />
                            PDF
                          </a>
                        )}
                        <button
                          onClick={() => handleDelete(item)}
                          disabled={deletingId === item.id}
                          className="inline-flex items-center gap-1.5 text-[#A1A1AA] hover:text-red-600 font-condensed uppercase text-xs tracking-[0.18em] font-bold transition-colors disabled:opacity-50"
                          data-testid={`intake-delete-${item.id}`}
                          title="Verwijder intake"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                          {deletingId === item.id ? "..." : "Verwijder"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Detail Modal */}
      <AnimatePresence>
        {detail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#1B1B3A]/80 backdrop-blur-sm flex items-center justify-center p-4 lg:p-10"
            onClick={closeDetail}
            data-testid="detail-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white border-2 border-[#1B1B3A] w-full max-w-4xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-[#1B1B3A] text-white px-6 py-5 flex items-start justify-between border-b-2 border-[#1B1B3A]">
                <div>
                  <div className="font-condensed uppercase text-[11px] tracking-[0.25em] text-[#3FCBA6] font-semibold mb-2">
                    · volledige intake ·
                  </div>
                  <div className="font-display text-2xl text-white leading-tight">
                    {detail.item.name || "Onbekende deelnemer"}
                  </div>
                  {detail.item.email && (
                    <div className="text-white/60 text-sm mt-1">{detail.item.email}</div>
                  )}
                </div>
                <button
                  onClick={closeDetail}
                  className="text-white/60 hover:text-[#3FCBA6] transition-colors"
                  aria-label="Sluiten"
                  data-testid="detail-close"
                >
                  <X className="w-6 h-6" strokeWidth={2.5} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-6 lg:p-8">
                {detailLoading ? (
                  <div className="text-center py-20 font-condensed uppercase text-sm tracking-[0.2em] text-[#6B6B85] font-semibold">
                    Laden...
                  </div>
                ) : detail.full ? (
                  <div className="space-y-6">
                    {/* Meta */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pb-6 border-b border-[#E8E8EE]">
                      <div>
                        <div className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold mb-1">Status</div>
                        <div className="font-display text-lg text-[#1B1B3A]">{detail.full.status === "completed" ? "Afgerond" : "Lopend"}</div>
                      </div>
                      <div>
                        <div className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold mb-1">Berichten</div>
                        <div className="font-display text-lg text-[#1B1B3A]">{(detail.full.messages || []).length}</div>
                      </div>
                      <div>
                        <div className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold mb-1">Gestart</div>
                        <div className="font-condensed text-sm text-[#1B1B3A]">
                          {new Date(detail.full.created_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })}
                        </div>
                      </div>
                      <div>
                        <div className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold mb-1">Rapport</div>
                        <div className="font-display text-lg text-[#1B1B3A]">
                          {detail.full.report ? "Ja" : "Nee"}
                        </div>
                      </div>
                    </div>

                    {/* Tracking / Session info */}
                    {(detail.full.ip_address || detail.full.user_agent || detail.full.referer || detail.full.country) && (
                      <div className="pb-6 border-b border-[#E8E8EE]">
                        <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#6B6B85] font-semibold mb-3">
                          · sessie-info ·
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                          {detail.full.ip_address && (
                            <div>
                              <span className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold">IP-adres: </span>
                              <span className="text-[#1B1B3A] font-mono" data-testid="detail-ip">{detail.full.ip_address}</span>
                            </div>
                          )}
                          {detail.full.country && (
                            <div>
                              <span className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold">Land: </span>
                              <span className="text-[#1B1B3A]" data-testid="detail-country">{detail.full.country}</span>
                            </div>
                          )}
                          {detail.full.referer && (
                            <div className="sm:col-span-2">
                              <span className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold">Kwam van: </span>
                              <span className="text-[#1B1B3A] break-all">{detail.full.referer}</span>
                            </div>
                          )}
                          {detail.full.user_agent && (
                            <div className="sm:col-span-2">
                              <span className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold">Browser: </span>
                              <span className="text-[#1B1B3A] text-xs break-all">{detail.full.user_agent}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Transcript */}
                    <div>
                      <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#6B6B85] font-semibold mb-4">
                        · gesprek ·
                      </div>
                      {(detail.full.messages || []).length === 0 ? (
                        <p className="text-[#6B6B85] italic">Geen berichten — deelnemer heeft de intake geopend maar niets ingevoerd.</p>
                      ) : (
                        <div className="space-y-4">
                          {detail.full.messages.map((m, i) => (
                            <div
                              key={i}
                              className={
                                m.role === "assistant"
                                  ? "border-l-4 border-[#3FCBA6] bg-[#F8F8FA] p-4"
                                  : "border-l-4 border-[#1B1B3A] bg-white p-4"
                              }
                            >
                              <div className="font-condensed uppercase text-[10px] tracking-[0.2em] font-semibold mb-1.5"
                                style={{ color: m.role === "assistant" ? "#3FCBA6" : "#1B1B3A" }}
                              >
                                {m.role === "assistant" ? "· AI · Techport ·" : "· Deelnemer ·"}
                              </div>
                              <div className="whitespace-pre-wrap text-[#1B1B3A] leading-relaxed text-sm">
                                {m.text}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Report summary */}
                    {detail.full.report && (
                      <div className="pt-6 border-t border-[#E8E8EE]">
                        <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#6B6B85] font-semibold mb-4">
                          · gegenereerd rapport ·
                        </div>
                        <div className="bg-[#F8F8FA] border border-[#E8E8EE] p-5 space-y-3">
                          <div>
                            <span className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold">Organisatie: </span>
                            <span className="text-[#1B1B3A]">{detail.full.report.organisatie || "—"}</span>
                          </div>
                          <div>
                            <span className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#6B6B85] font-semibold">Samenvatting: </span>
                            <span className="text-[#1B1B3A]">{detail.full.report.profiel?.samenvatting || "—"}</span>
                          </div>
                          <div className="pt-2">
                            <a
                              href={reportPdfUrl(detail.item.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tp-btn tp-btn--mint text-sm"
                              data-testid="detail-download-pdf"
                            >
                              <Download className="w-4 h-4" strokeWidth={2.5} />
                              Download volledig PDF rapport
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
